/**
 * Phase 3 — annotation + teaching layer Brain tools.
 *
 * Two tools live here (per `docs/scope/tactics-hub-roadmap.md` Phase 3):
 *  - draft_tactics_annotation   — produces zones / coaching points /
 *                                 constraints / voice-overs as a single
 *                                 union payload the coach commits.
 *  - summarise_tactics_pattern  — read tool returning a coach-script
 *                                 across keyframes + annotations, ready
 *                                 to read aloud or save as a voice-over
 *                                 transcript.
 *
 * The draft tool is a pure draft (no DB / storage writes); the apps-
 * side `commitTacticsDraft` in `apps/web2/src/lib/brainTactics.ts`
 * dispatches it via `applyAnnotationDraft` → `saveTacticsLayout`.
 *
 * The summarise tool is a read tool that uses the layout already in
 * screen context via `ctx.getTacticsLayout()` — no model echo of the
 * full layout through tool args required.
 */
import type { AnthropicTool } from '@4cm/core/ai';
import type {
  TacticsCoachingPoint,
  TacticsConstraint,
  TacticsKeyframe,
  TacticsLayout,
  TacticsVoiceover,
  TacticsZone,
} from '@4cm/core/tactics';

// World coordinate caps — pitch is 105 × 68, centred at origin. Mirrors
// the Phase 1 tactics tools so AI-hallucinated coords can't escape the
// pitch (the source of the "tokens float over the touchline" class of
// bugs called out in the roadmap's Risks section).
const X_LIMIT = 34;
const Z_LIMIT = 52.5;

// Coaching-point text cap (matches the schema comment in
// `packages/core/src/tactics/types.ts`).
const COACHING_POINT_MAX = 120;
// Constraint text cap — prevents the constraint card from becoming a
// novel; coaches who want narrative should use the voice-over.
const CONSTRAINT_MAX = 160;
// Transcript cap per keyframe — 10s of speech at ~150wpm ≈ 25 words ≈
// ~150 chars; we leave headroom for the coach to keep talking.
const TRANSCRIPT_MAX = 600;

// ---------------------------------------------------------------------------
// Wire-side input shapes (loose because the model speaks plain JSON;
// `applyAnnotationDraft` tightens these into the strict TacticsZone /
// TacticsCoachingPoint / etc. shapes).
// ---------------------------------------------------------------------------

export interface TacticsZoneInput {
  shape?: 'rect' | 'circle' | 'polygon';
  geom?: number[];
  fill?: string;
  label?: string;
}

export interface TacticsCoachingPointInput {
  at?: { x?: number; z?: number };
  text?: string;
  keyframe_id?: string;
}

export interface TacticsConstraintInput {
  text?: string;
  zone_id?: string;
}

export interface TacticsVoiceoverInput {
  keyframe_id?: string;
  transcript?: string;
  audio_url?: string;
}

export interface TacticsAnnotationDraftInput {
  persist_key?: string;
  zones?: TacticsZoneInput[];
  coaching_points?: TacticsCoachingPointInput[];
  constraints?: TacticsConstraintInput[];
  voiceovers?: TacticsVoiceoverInput[];
  /** Optional one-sentence intent for the preview card. */
  intent?: string;
}

// ---------------------------------------------------------------------------
// Tool schemas
// ---------------------------------------------------------------------------

export const TACTICS_DRAFT_ANNOTATION_TOOL: AnthropicTool = {
  name: 'draft_tactics_annotation',
  description:
    'Propose teaching-layer annotations for the current tactics board as a preview ' +
    'card for coach approval. Use whenever the coach asks for a coloured zone (press trap, ' +
    'cover shadow, attacking channel), a coaching-point bubble pinned to a position, a ' +
    'constraint card (drill rule), or a per-keyframe voice-over transcript. Emit ONLY the ' +
    'sub-types relevant to the prompt — do not invent unrelated annotations. Coordinates: ' +
    'world metres, pitch-centred; x ∈ [-34, +34] sideline, z ∈ [-52.5, +52.5] goal-line. ' +
    'This is a pure draft — nothing saves until the coach approves.',
  input_schema: {
    type: 'object',
    properties: {
      persist_key: {
        type: 'string',
        description:
          'localStorage key for the target board (e.g. "tactics-3d-{sessionId}"). ' +
          'Defaults to the active board from screen context.',
      },
      zones: {
        type: 'array',
        description:
          'Coloured zones overlaid on the pitch. Geometry depends on shape: ' +
          'rect → [cx, cz, width, depth]; circle → [cx, cz, radius]; ' +
          'polygon → flat [x, z, x, z, ...] in winding order.',
        items: {
          type: 'object',
          properties: {
            shape: { type: 'string', description: 'rect | circle | polygon' },
            geom: {
              type: 'array',
              items: { type: 'number' },
              description:
                'World-metre geometry. See zones[] description for the per-shape layout.',
            },
            fill: {
              type: 'string',
              description:
                'CSS hex/rgba fill — opacity baked in. Convention: #C0392B30 (technical red ~18%), ' +
                '#27AE6030 (physical green), #F39C1230 (psychological amber), #2980B930 (social blue), ' +
                '#f5f5f530 (neutral). The renderer applies a baseline 18% opacity floor regardless.',
            },
            label: {
              type: 'string',
              description: 'Optional short label rendered at the zone centroid.',
            },
          },
          required: ['shape', 'geom', 'fill'],
        },
      },
      coaching_points: {
        type: 'array',
        description:
          'Text bubbles pinned to pitch positions. Set keyframe_id to make a bubble ' +
          'appear only at that moment in the playback timeline.',
        items: {
          type: 'object',
          properties: {
            at: {
              type: 'object',
              properties: {
                x: { type: 'number' },
                z: { type: 'number' },
              },
              required: ['x', 'z'],
            },
            text: {
              type: 'string',
              description: `Coaching note. Max ${COACHING_POINT_MAX} chars — long-form belongs in the voice-over transcript.`,
            },
            keyframe_id: {
              type: 'string',
              description: 'Optional. When set, the bubble shows only at this keyframe.',
            },
          },
          required: ['at', 'text'],
        },
      },
      constraints: {
        type: 'array',
        description:
          'Drill rule cards — "must complete 3 passes in this zone before switching", etc. ' +
          'Optionally pin to a zone via zone_id.',
        items: {
          type: 'object',
          properties: {
            text: {
              type: 'string',
              description: `Constraint statement. Max ${CONSTRAINT_MAX} chars.`,
            },
            zone_id: {
              type: 'string',
              description: 'Optional. Pin the constraint to an existing zone.',
            },
          },
          required: ['text'],
        },
      },
      voiceovers: {
        type: 'array',
        description:
          'Per-keyframe voice-over transcripts. Audio recording is out of scope for the Brain — ' +
          'emit transcript only; the audio_url is filled in later if the coach records.',
        items: {
          type: 'object',
          properties: {
            keyframe_id: {
              type: 'string',
              description: 'Required — the keyframe this voice-over plays against.',
            },
            transcript: {
              type: 'string',
              description: `Plain-text transcript. Max ${TRANSCRIPT_MAX} chars per keyframe.`,
            },
            audio_url: {
              type: 'string',
              description: 'Optional Supabase storage URL. Usually omitted at draft time.',
            },
          },
          required: ['keyframe_id', 'transcript'],
        },
      },
      intent: {
        type: 'string',
        description:
          'One-sentence description of what these annotations teach — shown on the preview card ' +
          'so the coach knows what they\'re approving.',
      },
    },
  },
};

export const TACTICS_SUMMARISE_TOOL: AnthropicTool = {
  name: 'summarise_tactics_pattern',
  description:
    'Read the current tactics layout from screen context and return a structured coach-script: ' +
    'one section per keyframe (in order) with the keyframe label, voice-over transcript if set, ' +
    'and any coaching points scoped to that keyframe. Use when the coach wants to "explain this ' +
    'pattern out loud", "give me the talking points", or "what would I say to the players" — ' +
    'lets you compose a human-readable script without echoing the full layout through tool args. ' +
    'Does not mutate the board.',
  input_schema: {
    type: 'object',
    properties: {
      persist_key: {
        type: 'string',
        description: 'Defaults to the active board from screen context.',
      },
    },
  },
};

export const TACTICS_ANNOTATION_TOOLS: AnthropicTool[] = [
  TACTICS_DRAFT_ANNOTATION_TOOL,
  TACTICS_SUMMARISE_TOOL,
];

/** Names the brain dispatcher recognises for routing. */
export const TACTICS_ANNOTATION_TOOL_NAMES = new Set<string>([
  'draft_tactics_annotation',
  'summarise_tactics_pattern',
]);

// ---------------------------------------------------------------------------
// Apply: turn a draft payload into the v=3 annotation slices of a layout
// ---------------------------------------------------------------------------

function clampX(v: number): number {
  return Math.max(-X_LIMIT, Math.min(X_LIMIT, v));
}
function clampZ(v: number): number {
  return Math.max(-Z_LIMIT, Math.min(Z_LIMIT, v));
}

function genId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

function normaliseZone(input: TacticsZoneInput): TacticsZone | null {
  if (!input || !input.shape || !Array.isArray(input.geom)) return null;
  if (input.shape !== 'rect' && input.shape !== 'circle' && input.shape !== 'polygon') return null;
  // Per-shape geometry length check; reject malformed payloads rather
  // than try to "guess" what the model meant.
  const expectations: Record<TacticsZone['shape'], (geom: number[]) => boolean> = {
    rect: (g) => g.length === 4,
    circle: (g) => g.length === 3,
    polygon: (g) => g.length >= 6 && g.length % 2 === 0,
  };
  if (!expectations[input.shape](input.geom)) return null;
  if (!input.geom.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  const fill = typeof input.fill === 'string' && input.fill.trim().length > 0 ? input.fill : '#f5f5f530';
  // Clamp the *positional* components to the pitch; leave radii / sizes
  // alone (their semantics differ by shape).
  let geom = input.geom.slice();
  if (input.shape === 'rect') {
    geom = [clampX(geom[0]!), clampZ(geom[1]!), Math.max(0, geom[2]!), Math.max(0, geom[3]!)];
  } else if (input.shape === 'circle') {
    geom = [clampX(geom[0]!), clampZ(geom[1]!), Math.max(0, geom[2]!)];
  } else {
    // polygon — clamp each (x, z) pair.
    const clamped: number[] = [];
    for (let i = 0; i < geom.length; i += 2) {
      clamped.push(clampX(geom[i]!), clampZ(geom[i + 1]!));
    }
    geom = clamped;
  }
  return {
    id: genId('zone'),
    shape: input.shape,
    geom,
    fill,
    label: typeof input.label === 'string' ? input.label : undefined,
  };
}

function normaliseCoachingPoint(input: TacticsCoachingPointInput): TacticsCoachingPoint | null {
  if (!input || !input.at || typeof input.at.x !== 'number' || typeof input.at.z !== 'number') {
    return null;
  }
  if (typeof input.text !== 'string' || input.text.trim().length === 0) return null;
  return {
    id: genId('cp'),
    at: { x: clampX(input.at.x), z: clampZ(input.at.z) },
    text: input.text.slice(0, COACHING_POINT_MAX),
    keyframeId: typeof input.keyframe_id === 'string' ? input.keyframe_id : undefined,
  };
}

function normaliseConstraint(input: TacticsConstraintInput): TacticsConstraint | null {
  if (!input || typeof input.text !== 'string' || input.text.trim().length === 0) return null;
  return {
    id: genId('cst'),
    text: input.text.slice(0, CONSTRAINT_MAX),
    zoneId: typeof input.zone_id === 'string' ? input.zone_id : undefined,
  };
}

function normaliseVoiceover(input: TacticsVoiceoverInput): TacticsVoiceover | null {
  if (
    !input ||
    typeof input.keyframe_id !== 'string' ||
    input.keyframe_id.length === 0 ||
    typeof input.transcript !== 'string' ||
    input.transcript.trim().length === 0
  ) {
    return null;
  }
  return {
    keyframeId: input.keyframe_id,
    transcript: input.transcript.slice(0, TRANSCRIPT_MAX),
    audioUrl: typeof input.audio_url === 'string' ? input.audio_url : undefined,
  };
}

/**
 * Merge a draft of annotation slices into an existing layout. Arrays
 * APPEND — Phase 3 annotations are additive (you don't replace zones
 * when you add a new one). Voice-overs collapse on keyframeId so a
 * second voice-over for the same keyframe overwrites the first.
 *
 * Returns null when the draft is empty across every sub-type — caller
 * surfaces "draft had nothing usable" to the coach. Existing layout is
 * the source of truth for everything else (homeSpots, arrows, etc.);
 * we only touch the v=3 fields.
 */
export function applyAnnotationDraft(
  draft: TacticsAnnotationDraftInput,
  existing: TacticsLayout,
): TacticsLayout | null {
  const newZones = (draft.zones ?? [])
    .map(normaliseZone)
    .filter((z): z is TacticsZone => z !== null);
  const newCps = (draft.coaching_points ?? [])
    .map(normaliseCoachingPoint)
    .filter((c): c is TacticsCoachingPoint => c !== null);
  const newConstraints = (draft.constraints ?? [])
    .map(normaliseConstraint)
    .filter((c): c is TacticsConstraint => c !== null);
  const newVoiceovers = (draft.voiceovers ?? [])
    .map(normaliseVoiceover)
    .filter((v): v is TacticsVoiceover => v !== null);

  if (
    newZones.length === 0 &&
    newCps.length === 0 &&
    newConstraints.length === 0 &&
    newVoiceovers.length === 0
  ) {
    return null;
  }

  // Voice-overs collapse on keyframeId — later wins. This matches the
  // single-transcript-per-moment model in the schema (TacticsVoiceover
  // has no id field; keyframeId is the natural key).
  const voiceoverMap = new Map<string, TacticsVoiceover>();
  for (const v of existing.voiceovers ?? []) voiceoverMap.set(v.keyframeId, v);
  for (const v of newVoiceovers) voiceoverMap.set(v.keyframeId, v);

  return {
    ...existing,
    zones: [...(existing.zones ?? []), ...newZones],
    coachingPoints: [...(existing.coachingPoints ?? []), ...newCps],
    constraints: [...(existing.constraints ?? []), ...newConstraints],
    voiceovers: Array.from(voiceoverMap.values()),
  };
}

// ---------------------------------------------------------------------------
// Summarise — coach-script generation from a layout
// ---------------------------------------------------------------------------

export interface CoachScriptKeyframe {
  keyframe_id: string;
  label: string;
  t: number;
  transcript: string | null;
  coaching_points: Array<{ text: string; at: { x: number; z: number } }>;
}

export interface CoachScript {
  /** Top-level summary line — formation + intent. */
  headline: string;
  /** Ordered keyframe scripts. */
  keyframes: CoachScriptKeyframe[];
  /** Unscoped coaching points (no keyframeId) the coach can mention any time. */
  general_coaching_points: Array<{ text: string; at: { x: number; z: number } }>;
  /** Constraint card text — read once at the start of the drill. */
  constraints: string[];
  /** Flat plain-text script for direct TTS playback. */
  plain_text: string;
}

/**
 * Build a coach-script from the layout. Pure function — no AI call,
 * just deterministic composition over the existing keyframes + Phase 3
 * annotations.
 *
 * Order: keyframes by t ascending. Coaching points filtered per
 * keyframeId; unscoped ones bubble to `general_coaching_points` so the
 * model can choose where to weave them in.
 */
export function summariseTacticsLayout(layout: TacticsLayout): CoachScript {
  const keyframes = (layout.keyframes ?? []).slice().sort((a, b) => a.t - b.t);
  const voiceoverByKf = new Map<string, TacticsVoiceover>();
  for (const v of layout.voiceovers ?? []) voiceoverByKf.set(v.keyframeId, v);

  const cpByKf = new Map<string, TacticsCoachingPoint[]>();
  const generalCps: TacticsCoachingPoint[] = [];
  for (const cp of layout.coachingPoints ?? []) {
    if (cp.keyframeId) {
      const arr = cpByKf.get(cp.keyframeId) ?? [];
      arr.push(cp);
      cpByKf.set(cp.keyframeId, arr);
    } else {
      generalCps.push(cp);
    }
  }

  const kfScripts: CoachScriptKeyframe[] = keyframes.map((kf: TacticsKeyframe) => ({
    keyframe_id: kf.id,
    label: kf.label,
    t: kf.t,
    transcript: voiceoverByKf.get(kf.id)?.transcript ?? null,
    coaching_points: (cpByKf.get(kf.id) ?? []).map((cp) => ({ text: cp.text, at: cp.at })),
  }));

  const constraints = (layout.constraints ?? []).map((c) => c.text);

  const headlineParts = [layout.homeFormation];
  if (layout.awayFormation && layout.awayFormation !== layout.homeFormation) {
    headlineParts.push(`vs ${layout.awayFormation}`);
  }
  const headline = headlineParts.join(' ');

  // Plain-text version — useful for direct TTS playback. Built once
  // here so callers (Brain reply, voice-over capture, PDF export per
  // spec §10 / §17) all share the same script formatting.
  const lines: string[] = [];
  lines.push(headline);
  if (constraints.length > 0) {
    lines.push('');
    lines.push('Constraints:');
    for (const c of constraints) lines.push(`- ${c}`);
  }
  for (const kf of kfScripts) {
    lines.push('');
    lines.push(`${kf.label}:`);
    if (kf.transcript) lines.push(kf.transcript);
    for (const cp of kf.coaching_points) lines.push(`- ${cp.text}`);
  }
  if (generalCps.length > 0) {
    lines.push('');
    lines.push('General coaching points:');
    for (const cp of generalCps) lines.push(`- ${cp.text}`);
  }

  return {
    headline,
    keyframes: kfScripts,
    general_coaching_points: generalCps.map((cp) => ({ text: cp.text, at: cp.at })),
    constraints,
    plain_text: lines.join('\n'),
  };
}
